// src/components/auth/DeleteAccountModal.tsx
// Confirmation modal for permanent account deletion. Requires the user to type
// "DELETE" before the confirm button becomes active.

import React, { useEffect, useRef, useState } from 'react';

import { useAuthStore } from '../../stores/useAuthStore';
import { deleteUserAccount, ensureAnonymousAuth } from '../../services/authService';

const C = {
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
  error:   '#f87171',
  success: '#34d399',
};

export function DeleteAccountModal(): React.ReactElement | null {
  const open       = useAuthStore((s) => s.deleteModalOpen);
  const closeModal = useAuthStore((s) => s.closeDeleteModal);
  const profile    = useAuthStore((s) => s.profile);

  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMsg,   setErrorMsg]   = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setInputValue(''); setErrorMsg(null);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'Enter' && inputValue === 'DELETE') handleConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputValue]);

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleConfirm = async () => {
    if (inputValue !== 'DELETE' || isDeleting) return;
    setIsDeleting(true); setErrorMsg(null);
    try {
      await deleteUserAccount();
      closeModal();
      await ensureAnonymousAuth();
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : '';
      if (code === 'auth/requires-recent-login') {
        setErrorMsg('For security, please sign out and sign back in before deleting your account.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Account deletion failed. Please try again.');
      }
    } finally { setIsDeleting(false); }
  };

  if (!open) return null;

  const inputValid = inputValue.length === 0 ? null : inputValue === 'DELETE' ? true : false;
  const hasPremium = profile?.subscription?.status === 'active' || profile?.hasFullAccess === true;
  const canConfirm = inputValue === 'DELETE' && !isDeleting;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(16px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      role="dialog" aria-modal="true" aria-label="Delete account confirmation"
    >
      <div style={{
        background: '#0d1220',
        border: '1px solid rgba(248,113,113,0.2)',
        borderRadius: '20px',
        width: '100%', maxWidth: '420px',
        padding: '32px',
        display: 'flex', flexDirection: 'column', gap: '20px',
        boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Warning icon */}
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'rgba(248,113,113,0.1)',
          border: '1px solid rgba(248,113,113,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: C.text, margin: '0 0 10px' }}>
            Delete your account?
          </h2>
          <p style={{ color: C.muted, fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
            This will permanently delete your account, all progress data, and cannot be undone.
          </p>
        </div>

        {hasPremium && (
          <div style={{
            background: 'rgba(248,113,113,0.06)',
            border: '1px solid rgba(248,113,113,0.2)',
            borderRadius: '10px', padding: '12px 14px',
            fontSize: '0.82rem', color: C.error, lineHeight: 1.6,
          }}>
            <strong>You have an active subscription.</strong> Deleting your account will not
            automatically cancel your BOG payment. Please contact support if you need a refund.
          </div>
        )}

        <div>
          <label style={{ fontSize: '0.78rem', color: C.muted, marginBottom: '8px', display: 'block', letterSpacing: '0.04em' }} htmlFor="deleteConfirmInput">
            Type <strong style={{ color: C.error }}>DELETE</strong> to confirm
          </label>
          <input
            id="deleteConfirmInput" ref={inputRef}
            type="text" value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px',
              background: 'rgba(255,255,255,0.04)',
              border: `1.5px solid ${
                inputValid === null ? 'rgba(255,255,255,0.08)' :
                inputValid ? C.success : C.error
              }`,
              borderRadius: '10px', color: C.text,
              fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
              letterSpacing: '0.08em', textAlign: 'center', fontWeight: 700,
              transition: 'border-color 0.15s', fontFamily: 'inherit',
            }}
            autoComplete="off" spellCheck={false} aria-describedby="deleteHint"
          />
        </div>

        {errorMsg && <p style={{ fontSize: '0.82rem', color: C.error, textAlign: 'center', margin: 0 }} role="alert">{errorMsg}</p>}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            style={{
              flex: 1, padding: '12px', borderRadius: '999px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.08)',
              color: C.muted, fontSize: '0.88rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'opacity 0.15s',
            }}
            onClick={closeModal} disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            style={{
              flex: 1, padding: '12px', borderRadius: '999px',
              background: canConfirm ? C.error : 'rgba(248,113,113,0.3)',
              border: 'none', color: '#fff',
              fontSize: '0.88rem', fontWeight: 700,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.5,
              transition: 'all 0.15s', fontFamily: 'inherit',
            }}
            onClick={handleConfirm}
          >
            {isDeleting ? 'Deleting…' : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
